import { TypographyV3 } from '@jouzen/ecom-components';
import { getImageProps } from 'next/image';
import type { MessageKey } from 'types/LocaleMessageKey';
import type { SVGIconComponent } from 'types/svgr';
import { useTranslations } from 'use-intl';

import Image from '@/app/components/Image';

import type { LandingPageMembershipTabContentFeaturedImageProps } from './LandingPageMembershipTabContentFeaturedImage';

export type LandingPageMembershipTabBackground =
  | 'blue'
  | 'gray'
  | 'green'
  | 'red';

export interface LandingPageMembershipTabData {
  backgroundColor: LandingPageMembershipTabBackground;
  Icon: SVGIconComponent;
  id: string;
  tab: {
    label: MessageKey;
  };
  tabPanel: {
    cards: Omit<LandingPageMembershipTabContentCardProps, 'backgroundColor'>[];
    featuredImage: Omit<
      LandingPageMembershipTabContentFeaturedImageProps,
      'Icon'
    >;
  };
}

export interface LandingPageMembershipTabContentCardProps {
  readonly backgroundColor: LandingPageMembershipTabBackground;
  readonly copy: MessageKey;
  readonly image: {
    alt: MessageKey;
    src: MessageKey;
  };
  readonly newFeature?: boolean;
  readonly title: MessageKey;
}

const LandingPageMembershipTabContentCard = ({
  backgroundColor,
  copy,
  image,
  newFeature = false,
  title,
}: LandingPageMembershipTabContentCardProps): JSX.Element => {
  const t = useTranslations();
  const { props: backgroundImageProps } = getImageProps({
    alt: '',
    height: 300,
    src: `blue-sky/pop/pop_membership_card_bg_${backgroundColor}.png`,
    width: 300,
  });

  return (
    <div
      className="w-[285px] lg:w-[300px]"
      data-cy="landing-page-membership-card"
    >
      <div
        className="relative mb-6 aspect-square overflow-hidden rounded-md bg-cover"
        data-cy="landing-page-membership-card-img"
        style={{ backgroundImage: `url(${backgroundImageProps.src})` }}
      >
        {newFeature && (
          <div className="absolute left-4 top-4 rounded-full border border-solid border-current bg-white px-2 py-1 text-[0.625rem] uppercase leading-none tracking-wider text-slate-500 opacity-80">
            {t('pop_gen4_membership_card_new_feature')}
          </div>
        )}
        <Image
          data-cy="landing-page-membership-card-ui-img"
          localizedAlt={image.alt}
          format="png"
          height={300}
          localizedSrc={image.src}
          width={300}
        />
      </div>
      <TypographyV3 Element="h3" className="mb-3" variant="body-large">
        {t(title)}
      </TypographyV3>
      <TypographyV3 variant="body-small">{t.rich(copy)}</TypographyV3>
    </div>
  );
};

export default LandingPageMembershipTabContentCard;
